export interface PerformanceTrackingEntry<EntryType extends string> {
  type: EntryType;
}

export interface PerformanceTrackingEntryConfig<EntryType extends string> extends PerformanceTrackingEntry<EntryType> {
  onInit: (available: boolean) => void;
}

export class PerformanceTracker<EntryType extends string> {
  private entries: PerformanceTrackingEntry<EntryType>[] = [];

  private stats: {
    [K in EntryType]?: {
      count: number;
      duration: number;
    };
  } = {};

  constructor(options: { onInit: (available: boolean) => void; entries: PerformanceTrackingEntryConfig<EntryType>[] }) {
    this.entries = options.entries;
    let available = 'PerformanceObserver' in window && 'supportedEntryTypes' in PerformanceObserver;
    if (available) {
      try {
        const observer = new PerformanceObserver((list) => {
          list.getEntries().forEach((entry) => {
            const entryType = entry.entryType;

            if (!this.isValidType(entryType) || !('duration' in entry)) {
              return;
            }

            this.stats[entryType] ??= { count: 0, duration: 0 };
            this.stats[entryType].count += 1;
            this.stats[entryType].duration += entry.duration;
          });
        });

        options.entries.forEach((entry) => {
          let available = true;
          if (PerformanceObserver.supportedEntryTypes.includes(entry.type)) {
            observer.observe({ type: entry.type, buffered: true });
          } else {
            available = false;
          }
          entry.onInit(available);
        });
      } catch (error) {
        available = false;
      }
    }

    if (options.onInit) {
      options.onInit(available);
    }
  }

  getEntryStats(entryType: EntryType): { count: number; duration: number } {
    return this.stats[entryType] || { count: 0, duration: 0 };
  }

  isValidType(type: string): type is EntryType {
    return this.entries.some((entry) => entry.type === type);
  }
}
